import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { db } from '@/config/firebase'

export type TransactionType = 'deposit' | 'withdrawal' | 'bet' | 'win' | 'bonus'
export type TransactionStatus = 'pending' | 'completed' | 'rejected' | 'cancelled'

export interface Transaction {
  id: string
  userId: string
  type: TransactionType
  amount: number
  status: TransactionStatus
  description?: string
  paymentMethod?: string
  reference?: string
  createdAt: Date
  completedAt?: Date
}

// Create a new deposit request
export async function createDepositRequest(
  userId: string,
  amount: number,
  paymentMethod: string,
  reference: string
): Promise<{ success: boolean; transactionId?: string; error?: string }> {
  try {
    const docRef = await addDoc(collection(db, 'transactions'), {
      userId,
      type: 'deposit',
      amount,
      status: 'pending',
      paymentMethod,
      reference,
      description: `Depósito via ${paymentMethod}`,
      createdAt: serverTimestamp(),
    })

    return { success: true, transactionId: docRef.id }
  } catch (error) {
    console.error('Error creating deposit:', error)
    return { success: false, error: 'Error al crear la solicitud de depósito' }
  }
}

// Create a withdrawal request
export async function createWithdrawalRequest(
  userId: string,
  amount: number,
  paymentMethod: string,
  accountDetails: string
): Promise<{ success: boolean; transactionId?: string; error?: string }> {
  try {
    const docRef = await addDoc(collection(db, 'transactions'), {
      userId,
      type: 'withdrawal',
      amount,
      status: 'pending',
      paymentMethod,
      accountDetails,
      description: `Retiro via ${paymentMethod}`,
      createdAt: serverTimestamp(),
    })

    return { success: true, transactionId: docRef.id }
  } catch (error) {
    console.error('Error creating withdrawal:', error)
    return { success: false, error: 'Error al crear la solicitud de retiro' }
  }
}

// Get user transaction history
export async function getUserTransactions(userId: string): Promise<Transaction[]> {
  try {
    const q = query(
      collection(db, 'transactions'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => {
      const data = doc.data()
      return {
        id: doc.id,
        userId: data.userId,
        type: data.type,
        amount: data.amount,
        status: data.status,
        description: data.description,
        paymentMethod: data.paymentMethod,
        reference: data.reference,
        createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
        completedAt: data.completedAt ? (data.completedAt as Timestamp).toDate() : undefined,
      } as Transaction
    })
  } catch (error) {
    console.error('Error getting transactions:', error)
    return []
  }
}

// Format transaction type for display
export function formatTransactionType(type: TransactionType): string {
  const types: Record<TransactionType, string> = {
    deposit: 'Depósito',
    withdrawal: 'Retiro',
    bet: 'Apuesta',
    win: 'Ganancia',
    bonus: 'Bonus',
  }
  return types[type] || type
}

// Format transaction status for display
export function formatTransactionStatus(status: TransactionStatus): string {
  const statuses: Record<TransactionStatus, string> = {
    pending: 'Pendiente',
    completed: 'Completado',
    rejected: 'Rechazado',
    cancelled: 'Cancelado',
  }
  return statuses[status] || status
}

// Get status color
export function getStatusColor(status: TransactionStatus): string {
  const colors: Record<TransactionStatus, string> = {
    pending: 'text-yellow-500',
    completed: 'text-green-500',
    rejected: 'text-red-500',
    cancelled: 'text-gray-500',
  }
  return colors[status] || 'text-gray-400'
}
